import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import CardActualizacionMasiva from "../../../General/Excel/CardActualizacionMasiva";
import MiniSpinner from '../../../General/MiniSpinner';
import {
  base64ToArrayBufferXlsx,
  saveByteArrayXlsx
} from '../../../../utilities/formatFile';
import {
  createPublicacionApi,
  fetchExportarCategoriasApi,
  fetchExportarMarcasApi
} from '../../../../api/axios';
import { canShowCreacionMasivaWizard } from '../../../../utilities/masivaWizardAccess';
import {
  buildMasivaBulkRequestFromCsvText,
  extractCreacionMasivaBulkResult,
  formatCreacionMasivaBulkError
} from '../../../../utilities/creacionMasivaCsvBulk';
import { toast } from '../../../../utilities/appToast';
import PropTypes from 'prop-types';

const ADMIN_TYPES = [1, 2];

const CreacionMasivaPublicaciones = ({ tipoUsuario, userType, userId, idSeller }) => {
  const [file, setFile] = useState();
  const [isLoading, setIsLoading] = useState({
    marcas: false,
    categorias: false,
    postPublicaciones: false
  });
  const navigate = useNavigate();
  const showWizardLink = canShowCreacionMasivaWizard(userType);

  const isLoadingHandler = (name, state) => {
    setIsLoading(prevState => ({ ...prevState, [name]: state }));
  };

  const downloadXlsMarcas = () => {
    isLoadingHandler("marcas", true);

    const req = fetchExportarMarcasApi();
    req.then(res => {
      const excel = res?.excel ?? res?.excelBase64;
      if (excel) {
        let blob = base64ToArrayBufferXlsx(excel);
        saveByteArrayXlsx('Marcas.xlsx', blob);
      }
      toast.success(res.message ?? "Exportación exitosa");
    }).catch(err => {
      toast.warn(err.message ?? "No se pudo descargar");
    }).finally(() => {
      isLoadingHandler("marcas", false);;
    });
  };

  const downloadXlsCategorias = () => {
    isLoadingHandler("categorias", true);

    const req = fetchExportarCategoriasApi();
    req.then(res => {
      const excel = res?.excel ?? res?.excelBase64;
      if (excel) {
        let blob = base64ToArrayBufferXlsx(excel);
        saveByteArrayXlsx('Categorias SubFamilias.xlsx', blob)
      }
      toast.success(res.message ?? "Exportación exitosa");
    }).catch(err => {
      toast.warn(err.message ?? "No se pudo descargar");
    }).finally(() => {
      isLoadingHandler("categorias", false);
    });
  }

  const isValidUpdateExcel = () => {
    if (!file) {
      toast.warn('No ha seleccionado ningún archivo CSV.');
      return false;
    }
    return true;
  }

    const exportExcel = async () => {
        isLoadingHandler("postPublicaciones", true);

        try {
            const fileName = String(file.name ?? '').toLowerCase();
            if (!fileName.endsWith('.csv')) {
                toast.warn('Seleccioná un archivo CSV con el formato de creación masiva.');
                return;
            }

            const text = await file.text();
            const request = buildMasivaBulkRequestFromCsvText(text, {
                userId,
                currentSellerId: idSeller,
                isPrivilegedUser: ADMIN_TYPES.includes(Number(tipoUsuario))
            });

            if (!request.masivaBulks.length) {
                toast.warn('No encontramos filas válidas para importar.');
                return;
            }

            const response = await createPublicacionApi(request);
            const { created: publicadas, errors: errores } = extractCreacionMasivaBulkResult(response);

            if (publicadas.length > 0 && errores.length > 0) {
                toast.success(`${publicadas.length} publicación(es) creada(s) con éxito.`);
                publicadas.forEach((pub) => {
                    const idPublicacion = pub?.idPublicacion ?? pub?.IdPublicacion;
                    if (idPublicacion) {
                        toast.success(`Publicación creada: ${idPublicacion}`);
                    }
                });
                errores.forEach((err) => toast.warn(formatCreacionMasivaBulkError(err)));
            } else if (publicadas.length > 0 && errores.length === 0) {
                toast.success(`${publicadas.length} publicación(es) creadas con éxito.`);
                navigate("/publicaciones");
            } else if (publicadas.length === 0 && errores.length > 0) {
                toast.error(`Ninguna publicación se creó. (${errores.length} error(es))`);
                errores.forEach((err) => toast.warn(formatCreacionMasivaBulkError(err)));
            } else {
                toast.error('No se recibió confirmación de creación desde el servidor.');
            }
        } catch (err) {
            const apiErrors = extractCreacionMasivaBulkResult(err?.response?.data ?? {}).errors;
            if (apiErrors.length > 0) {
                apiErrors.forEach((e) => toast.warn(formatCreacionMasivaBulkError(e)));
            } else if (Array.isArray(err?.response?.data?.errores)) {
                err.response.data.errores.forEach((e) => toast.warn(formatCreacionMasivaBulkError(e)));
            } else {
                toast.error("Las publicaciones no pudieron ser creadas.");
            }
        } finally {
            isLoadingHandler("postPublicaciones", false);
        }
    };

  if (tipoUsuario === 6) {
    return null;
  }

  return (
    <div className="container">
      <div className="row justify-content-between align-items-center page-titles text-center text-md-start">
        <div className="col-12 mb-4">
          <div
            className="row mb-3 mb-md-4 align-items-center py-3 px-2 rounded"
            style={{
              background: 'linear-gradient(to right, #f8f9fa 0%, #fff 100%)',
              borderLeft: '4px solid var(--rojo-megatone, #c00)'
            }}
          >
            <div className="col-12 col-md-8">
              <h4 className="mb-0 text-themecolor color-rojo-megatone fw-bold">Creación masiva por CSV</h4>
              <p className="mb-0 mt-1 small text-muted">
                Descargá el formato CSV, completá el archivo y volvé a importarlo para crear publicaciones.
              </p>
            </div>
            <div className="col-12 col-md-4 d-flex flex-wrap gap-2 justify-content-md-end mt-3 mt-md-0">
              {showWizardLink && (
                <Link to="/Publicaciones/CreacionMasiva" className="btn btn-danger btn-sm">
                  Ir al wizard guiado
                </Link>
              )}
              <Link to="/Publicaciones" className="btn btn-outline-secondary btn-sm">
                Volver al listado
              </Link>
            </div>
          </div>
        </div>
        <div className="card p-4 shadow-sm">
          <CardActualizacionMasiva
            title="Publicaciones"
            btnText="Crear Publicaciones"
            tipoExcel={4}
            additionalHeader={(
              <div className="col-auto">
                <div className="row my-md-0 my-sm-2">
                  <div className="col-12 col-sm-auto my-2 my-sm-0">
                    <button
                      onClick={downloadXlsMarcas}
                      className="btn btn-sm btn-green"
                      disabled={isLoading.marcas}
                    >
                      Descargar Marcas
                    </button>
                  </div>
                  <div className="col-auto" hidden={!isLoading.marcas}>
                    <MiniSpinner />
                  </div>
                  <div className="col-12 col-sm-auto my-0 my-sm-0">
                    <button
                      onClick={downloadXlsCategorias}
                      className="btn btn-sm btn-green"
                      disabled={isLoading.categorias}
                    >
                      Descargar Categorías
                    </button>
                  </div>
                  <div className="col-auto" hidden={!isLoading.categorias}>
                    <MiniSpinner />
                  </div>
                </div>
              </div>
            )}
            isLoading={isLoading.postPublicaciones}
            updateFile={setFile}
            exportExcel={exportExcel}
            isValidUpdateExcel={isValidUpdateExcel}
            downloadName="Modelo Creación Masiva de Publicaciones"
          />
        </div>
      </div>
    </div>
  );
};
CreacionMasivaPublicaciones.propTypes = {
    tipoUsuario: PropTypes.number,
    userId: PropTypes.number,
    idSeller: PropTypes.number,
    userType: PropTypes.shape({
        isAdminMod: PropTypes.bool,
        isCategory: PropTypes.bool
    })
}

const mapStateToProps = (state) => ({
  tipoUsuario: state.general.tipoUsuario,
  userId: state.general.userId,
  idSeller: state.general.idSeller,
  userType: state.general.userType
});

export default connect(mapStateToProps)(CreacionMasivaPublicaciones);
